import { DateTime as LuxonDateTime, type DurationObjectUnits } from 'luxon'
import { getTimezoneUtcOffset } from '@/utils/date/getter.js'

export type TimeFormats = (typeof TimeFormat)[keyof typeof TimeFormat]
export const TimeFormat = {
  ['h:mm a']: 'h:mm a', // 8:45 AM
  ['HH:mm']: 'HH:mm', // 08:45
  ['H.mm']: 'H.mm', // 8.45
} as const

type DurationLabels = {
  minute: string
  minutes: string
  hour: string
  hours: string
}

/**
 * Finds the next rounded time based on the given time string and interval.
 *
 * @param time - The input time in 'HH:mm' format.
 * @param interval - The interval in minutes to which the time should be rounded.
 * @returns - The next rounded time in 'HH:mm' format.
 */
export const findNextRoundedTime = (time: string, interval: number) => {
  let dt = formatToTime(time, 'HH:mm')

  const minutes = dt.minute

  if (minutes < interval) {
    dt = dt.set({ minute: interval })
  } else {
    dt = dt.plus({ hours: 1 }).set({ minute: 0 })
  }

  return dt.toFormat(TimeFormat['HH:mm'])
}

/**
 * Checks if the given time range is valid based on the specified interval.
 *
 * @param startTime - The start time in the specified format.
 * @param endTime - The end time in the specified format.
 * @param interva - The interval in minutes that the time range should be checked against.
 * @param format - The format of the provided times.
 * @returns - Returns true if the time range is valid and the interval is within the specified duration, otherwise false.
 */
export const isValidTimeRange = (
  startTime: string,
  endTime: string,
  interval: number,
  format: TimeFormats,
) => {
  // Parse the start time and end time using the provided format
  const dateTimeStart = LuxonDateTime.fromFormat(startTime, format)
  const dateTimeEnd = LuxonDateTime.fromFormat(endTime, format)

  // Calculate the duration between the two times in minutes
  const duration = dateTimeEnd.diff(dateTimeStart, ['minutes'])

  // Check if both parsed times are valid and interval is greater than the duration in minutes
  if (
    !dateTimeStart.isValid ||
    !dateTimeEnd.isValid ||
    interval > duration.as('minutes')
  ) {
    return false
  }

  return true
}

/**
 * Format input value according to the passed format.
 *
 * @param value Input time string.
 * @param format Input format of the time string.
 * @returns Formatted time string in passed format.
 */
export const formatToTime = (value: string, format: string) =>
  LuxonDateTime.fromFormat(value, format)

/**
 * Format input value to ISO time in HH:mm format.
 *
 * @param value Input time string.
 * @param format Input format of the time string.
 * @returns Formatted time string in HH:mm format.
 */
export const formatToISOTime = (time: string, format: string) => {
  const timeObject = LuxonDateTime.fromFormat(time, format)
  return timeObject.isValid ? timeObject.toFormat(TimeFormat['HH:mm']) : ''
}

/**
 * Generate array of available times based on provided parameters.
 *
 * @param startTime Start time in HH:mm format.
 * @param endTime End time in HH:mm format.
 * @param interval Interval in minutes between each time slot.
 * @param format Desired output format.
 * @returns Array of available times formatted according to the specified format.
 */
export const availableTimes = (
  startTime: string,
  endTime: string,
  interval: number,
  format: string,
  durationReference?: string,
  durationTimeLabel?: DurationLabels,
) => {
  const times = []
  const startTimeObject = LuxonDateTime.fromFormat(startTime, 'HH:mm')
  const endTimeObject = LuxonDateTime.fromFormat(endTime, 'HH:mm')
  let currentTimeObj = startTimeObject

  while (currentTimeObj <= endTimeObject) {
    let duration
    if (durationReference) {
      const durationObject = calculateTimeDuration(
        durationReference,
        currentTimeObj.toFormat(TimeFormat['HH:mm']),
      )

      if (durationObject && durationTimeLabel) {
        duration = durationToString(durationObject, durationTimeLabel)
      }
    }
    times.push({ value: currentTimeObj.toFormat(format), duration })
    currentTimeObj = currentTimeObj.plus({ minutes: interval })
  }

  return times
}

/**
 * Retrieves the current timezone offset in the format "HH:mm".
 * The offset is based on luxon settings.
 *
 * @returns The timezone offset in the format "HH:mm".
 */
export const getTimezoneOffset = () => {
  const offset = getTimezoneUtcOffset()

  const hours = Math.floor(offset / 60)
  const minutes = Math.abs(offset % 60)

  return `${hours}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Calculates the duration between two times given in a specified format.
 * @param startTime - The start time in the specified format.
 * @param endTime - The end time in the specified format.
 * @param format - The format of the input times. Defaults to 'HH:mm'.
 * @returns - An object containing the duration in hours and minutes.
 */
export const calculateTimeDuration = (
  startTime: string,
  endTime: string,
  format: TimeFormats = 'HH:mm',
) => {
  const dateTimeStart = LuxonDateTime.fromFormat(startTime, format)
  const dateTimeEnd = LuxonDateTime.fromFormat(endTime, format)

  // Calculate the duration between the two times
  const duration = dateTimeEnd.diff(dateTimeStart, ['hours', 'minutes'])

  if (duration.valueOf() <= 0) {
    return
  }

  return duration.toObject()
}

/**
 * Converts a duration object into a string.
 * @param duration - An object containing the duration in hours and minutes.
 * @param labels - An object containing the labels for singular and plural forms of minutes and hours.
 * @returns - A string representing the duration.
 */
export const durationToString = (
  duration: DurationObjectUnits,
  labels: DurationLabels,
) => {
  let hours = ''
  let minutes = ''

  if (duration.hours) {
    hours = `${duration.hours} ${duration.hours === 1 ? labels.hour : labels.hours}`
  }

  if (duration.minutes) {
    minutes = `${duration.minutes} ${duration.minutes === 1 ? labels.minute : labels.minutes}`
  }

  return [hours, minutes].filter(Boolean).join(' ')
}

/**
 * Parses a timer string and converts it to total seconds.
 * Supports multiple formats: HH:MM:SS, MM:SS, or SS.
 * Invalid or negative numbers are treated as 0.
 *
 * @param timer - The timer string to parse (e.g., "1:30:45", "5:30", "45").
 * @returns - The total number of seconds.
 */
export const parseTimerToSeconds = (timer: string): number => {
  const timerParts = timer?.trim().split(':')
  if (!timerParts) {
    return 0
  }

  const parts = timerParts.map((part) => {
    const number = Number(part.trim())
    return Number.isNaN(number) || number < 0 ? 0 : number
  })

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts
    return hours * 3600 + minutes * 60 + seconds
  }

  if (parts.length === 2) {
    const [minutes, seconds] = parts
    return minutes * 60 + seconds
  }

  if (parts.length === 1) {
    const [seconds] = parts
    return seconds
  }

  return 0
}
